import React, { useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import HomeIcon from '@mui/icons-material/Home';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { Seo } from 'src/components/seo';
import { DataHelper } from 'src/services/dataHelper';
import { AddItemToCarts } from './hooks/addItemToCarts';
import Loader from 'src/pages/components/loader';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';

// Helper: format as AED currency
function formatCurrency(value) {
    return value?.toLocaleString('en-US', { style: 'currency', currency: 'AED' }) || 'AED 0.00';
}

// Helper: avoid division issues
function safeDivide(numerator, denominator) {
    const n = Number(numerator);
    const d = Number(denominator);
    if (!Number.isFinite(n) || !Number.isFinite(d) || d === 0) return 0;
    return n / d;
}

export default function OrderSummary() {
    const [items, setItems] = useState([]);
    const [loader, setLoader] = useState(false);
    const [coupon, setCoupon] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const location = useLocation();
    const [updatingItemId, setUpdatingItemId] = useState(null);
    const [addresses, setAddresses] = useState([
        {
            id: 1,
            name: 'Harisul Islam',
            phone: '+971521147706',
            addressLine: 'Nagaon Village, Near Masjid Road',
            city: 'Abu Dhabi',
            country: 'UAE',
        },
        {
            id: 2,
            name: 'Office Address',
            phone: '+971521147706',
            addressLine: 'Corniche Street, City Center Tower',
            city: 'Abu Dhabi',
            country: 'UAE',
        },
    ]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [addressDialogOpen, setAddressDialogOpen] = useState(false);
    const [newAddress, setNewAddress] = useState({
        name: '',
        phone: '',
        addressLine: '',
        city: '',
        country: '',
    });

    const navigate = useNavigate();

    // 🔹 Fetch cart/order data from API
    const getOrderSummaryData = async () => {
        try {
            setLoader(true);
            const ID = location?.state?.ID;
            if (ID) {
                console.log(ID);
                const response = await new DataHelper().operationsDetailsByOrderStatus('Operations', ID, 'MoveToCart');


                if (response?.status === 200) {
                    const apiItems =
                        response.data?.details?.map((d) => ({
                            ID: d.ID,
                            itemId: d.itemId,
                            itemName: d.itemName,
                            image: d.image,
                            price: safeDivide(d.price, d.qty),
                            lineTotal: safeDivide(d.lineTotal, d.qty),
                            lineNetAmount: safeDivide(d.lineNetAmount, d.qty),
                            qty: Number(d.qty) || 0,
                            discountAmount: safeDivide(d.discountAmount, d.qty),
                            taxAmount: safeDivide(d.taxAmount, d.qty),
                        })) || [];
                    setItems(apiItems);
                }
            }
        } catch (err) {
            console.error('Error fetching order summary:', err);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        getOrderSummaryData();
        setSelectedAddress(addresses[0]);
    }, []);

    // 🔹 Totals
    const subtotal = useMemo(() => items.reduce((s, it) => s + it.lineTotal * it.qty, 0), [items]);
    const discountAmount = useMemo(() => items.reduce((s, it) => s + (it.discountAmount * it.qty || 0), 0), [items]);
    const tax = useMemo(() => items.reduce((s, it) => s + (it.taxAmount * it.qty || 0), 0), [items]);
    const shipping = 0;
    const total = useMemo(() => items.reduce((s, it) => s + it.lineNetAmount * it.qty, 0), [items]);

    // 🔹 Update Quantity
    const updateQty = async (ID, delta) => {
        try {
            if (delta >= 1) {
                setLoader(true);
                setUpdatingItemId(ID); // show loader for this item
                const response = await new DataHelper().getById('Items', ID);
                const itemData = response?.data;
                await new AddItemToCarts().handleAddToCart(null, itemData, delta, 'Updated Quantity', 'ItemUpdatedFromBuyNow', 'BuyNow');
                await getOrderSummaryData();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoader(false);
            setUpdatingItemId(null); // hide loader for this item
        }
    };

    // 🔹 Remove Item
    const removeItem = async (ID, qty) => {
        const response = await new DataHelper().getById('Items', ID);
        const itemData = response?.data;
        await new AddItemToCarts().handleAddToCart(null, itemData, qty, 'Item Removed', 'ItemDelete', 'MoveToCart');
        await getOrderSummaryData();
    };

    // 🔹 Save for Later
    const saveForLater = async (ID, qty) => {
        const response = await new DataHelper().getById('Items', ID);
        const itemData = response?.data;
        await new AddItemToCarts().handleAddToCart(null, itemData, qty, 'Saved for Later', 'ItemUpdated', 'SaveForLater');
        await getOrderSummaryData();
    };

    // 🔹 Confirm Order
    const handleConfirmOrder = async () => {
        try {
            setLoader(true);
            const result = await new DataHelper().operationsDetailsByOrderStatus('Operations', null, 'OrderPlaced');
            if (result?.status === 200) {
                toast.success('Order placed successfully!');
                navigate('/thank-you');
            }
        } catch (err) {
            console.error('Error confirming order:', err);
            toast.error('Failed to confirm order.');
        } finally {
            setLoader(false);
        }
    };

    // 🔹 Coupon handling
    const applyCoupon = () => {
        const code = coupon.trim().toUpperCase();
        if (code === 'SAVE10') setAppliedCoupon({ code: 'SAVE10', label: '10% off (max AED 10)' });
        else if (code === 'FREESHIP') setAppliedCoupon({ code: 'FREESHIP', label: 'Free shipping' });
        else {
            setAppliedCoupon(null);
            toast.error('Invalid coupon code');
        }
    };
    const clearCoupon = () => {
        setAppliedCoupon(null);
        setCoupon('');
    };

    // 🔹 Address handling
    const handleSelectAddress = (addr) => {
        setSelectedAddress(addr);
        setAddressDialogOpen(false);
    };
    const handleAddNewAddress = () => {
        if (!newAddress.name || !newAddress.addressLine) return;
        const id = addresses.length + 1;
        const addr = { id, ...newAddress };
        setAddresses([...addresses, addr]);
        handleSelectAddress(addr);
        setNewAddress({ name: '', phone: '', addressLine: '', city: '', country: '' });
    };

    return (
        <>
            <Seo title="Order Summary" />
            <Box sx={{ flexGrow: 1, py: 0 }}>
                <Container maxWidth="xl">
                    <Stack spacing={0}>
                        <Typography variant="h5" mb={1}>Order Summary</Typography>
                        <Grid container spacing={2} sx={{ flexWrap: 'wrap-reverse' }}>
                            {/* LEFT SIDE */}
                            <Grid item xs={12} md={8}>
                                {/* Address */}
                                <Card sx={{ p: 1, mb: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                                            <HomeIcon /> Shipping Address
                                        </Typography>
                                        <Button onClick={() => setAddressDialogOpen(true)}>Change Address</Button>
                                    </Stack>

                                    {selectedAddress && (
                                        <Paper elevation={1} sx={{ p: 2, mt: 1 }}>
                                            <Typography variant="subtitle1" fontWeight="bold">{selectedAddress.name}</Typography>
                                            <Typography variant="body2" color="text.secondary">{selectedAddress.addressLine}</Typography>
                                            <Typography variant="body2" color="text.secondary">{selectedAddress.city}, {selectedAddress.country}</Typography>
                                            <Typography variant="body2">📞 {selectedAddress.phone}</Typography>
                                        </Paper>
                                    )}
                                </Card>

                                {/* Items */}
                                <Card sx={{ p: 0 }}>
                                    <CardContent sx={{ pt: 0 }}>
                                        <List>
                                            {items.length === 0 && (
                                                <ListItem>
                                                    <ListItemText primary="Your order is empty." />
                                                </ListItem>
                                            )}

                                            {items.map((item) => (
                                                <React.Fragment key={item.id}>
                                                    <ListItem
                                                        alignItems="center"
                                                        sx={{
                                                            flexDirection: { xs: "column", sm: "row" },
                                                            justifyContent: "space-between",
                                                            gap: { xs: 1, sm: 2 },
                                                            px: { xs: 0.5, sm: 1 },
                                                            py: { xs: 1, sm: 1.5 },
                                                        }}
                                                    >
                                                        {/* LEFT SIDE: IMAGE + DETAILS */}
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            spacing={1.5}
                                                            sx={{
                                                                flex: 1,
                                                                minWidth: 180,
                                                                textAlign: { xs: "center", sm: "left" },
                                                                bgcolor: "background.paper",
                                                                borderRadius: 3,
                                                                p: { xs: 1.5, sm: 2 },
                                                                border: "1px solid",
                                                                borderColor: "divider",
                                                                transition: "all 0.2s ease-in-out",
                                                                "&:hover": {
                                                                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                                                                    transform: "translateY(-2px)",
                                                                },
                                                            }}
                                                        >
                                                            {/* IMAGE */}
                                                            <Box
                                                                sx={{
                                                                    width: { xs: 56, sm: 64 },
                                                                    height: { xs: 76, sm: 84 },
                                                                    borderRadius: 2,
                                                                    overflow: "hidden",
                                                                    border: "1px solid #e0e0e0",
                                                                    flexShrink: 0,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    backgroundColor: "#fafafa",
                                                                }}
                                                            >
                                                                <Box
                                                                    component="img"
                                                                    src={item.image || "/no-image.png"}
                                                                    alt={item.itemName}
                                                                    sx={{
                                                                        width: "100%",
                                                                        height: "100%",
                                                                        objectFit: "cover",
                                                                        display: "block",
                                                                    }}
                                                                />
                                                            </Box>

                                                            {/* ITEM INFO */}
                                                            <Box sx={{ flex: 1 }}>
                                                                {/* 🏷️ Product Name */}
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        fontSize: { xs: "0.95rem", sm: "1rem" },
                                                                        color: "text.primary",
                                                                        mb: 0.3,
                                                                        lineHeight: 1.4,
                                                                    }}
                                                                >
                                                                    {item.itemName}
                                                                </Typography>

                                                                {/* 💰 Price */}
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 500,
                                                                        color: "primary.main",
                                                                        fontSize: { xs: "0.9rem", sm: "0.95rem" },
                                                                    }}
                                                                >
                                                                    {formatCurrency(item.lineNetAmount)}
                                                                </Typography>

                                                                {/* 🔢 Quantity Controls + Loader */}
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1.2}
                                                                    alignItems="center"
                                                                    mt={1.4}
                                                                    sx={{
                                                                        flexWrap: "nowrap",
                                                                        justifyContent: { xs: "center", sm: "flex-start" },
                                                                    }}
                                                                >
                                                                    {/* ➖ Decrease Button */}
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => updateQty(item.itemId, item.qty - 1)}
                                                                        sx={(theme) => ({
                                                                            width: 36,
                                                                            height: 36,
                                                                            borderRadius: 2,
                                                                            border: "1px solid",
                                                                            borderColor: theme.palette.divider,
                                                                            color:
                                                                                theme.palette.mode === "light"
                                                                                    ? theme.palette.text.primary
                                                                                    : theme.palette.grey[200],
                                                                            bgcolor:
                                                                                theme.palette.mode === "light"
                                                                                    ? theme.palette.background.paper
                                                                                    : theme.palette.background.default,
                                                                            boxShadow:
                                                                                theme.palette.mode === "light"
                                                                                    ? "0 1px 3px rgba(0,0,0,0.08)"
                                                                                    : "0 1px 3px rgba(0,0,0,0.4)",
                                                                            transition: "all 0.25s ease-in-out",
                                                                            "&:hover": {
                                                                                bgcolor: theme.palette.primary.main,
                                                                                color: theme.palette.primary.contrastText,
                                                                                borderColor: theme.palette.primary.main,
                                                                                transform: "translateY(-2px)",
                                                                                boxShadow:
                                                                                    theme.palette.mode === "light"
                                                                                        ? "0 3px 8px rgba(0,0,0,0.15)"
                                                                                        : "0 3px 8px rgba(0,0,0,0.6)",
                                                                            },
                                                                            "&:active": {
                                                                                transform: "scale(0.96)",
                                                                            },
                                                                        })}
                                                                    >
                                                                        <RemoveIcon fontSize="small" />
                                                                    </IconButton>

                                                                    {/* 🔢 Quantity Input */}
                                                                    <TextField
                                                                        value={item.qty}
                                                                        size="small"
                                                                        inputProps={{
                                                                            style: {
                                                                                textAlign: "center",
                                                                                width: 46,
                                                                                fontWeight: 600,
                                                                                fontSize: "0.9rem",
                                                                            },
                                                                        }}
                                                                        sx={(theme) => ({
                                                                            "& .MuiOutlinedInput-root": {
                                                                                borderRadius: 2,
                                                                                height: 38,
                                                                                bgcolor:
                                                                                    theme.palette.mode === "light"
                                                                                        ? "#fff"
                                                                                        : theme.palette.background.default,
                                                                                "& fieldset": {
                                                                                    borderColor: theme.palette.divider,
                                                                                },
                                                                                "&:hover fieldset": {
                                                                                    borderColor: theme.palette.text.secondary,
                                                                                },
                                                                                "&.Mui-focused fieldset": {
                                                                                    borderColor: theme.palette.primary.main,
                                                                                    boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`,
                                                                                },
                                                                            },
                                                                            "& input": { p: 0 },
                                                                            transition: "all 0.2s ease-in-out",
                                                                        })}
                                                                    />

                                                                    {/* ➕ Increase Button */}
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => updateQty(item.itemId, item.qty + 1)}
                                                                        sx={(theme) => ({
                                                                            width: 36,
                                                                            height: 36,
                                                                            borderRadius: 2,
                                                                            border: "1px solid",
                                                                            borderColor: theme.palette.divider,
                                                                            color:
                                                                                theme.palette.mode === "light"
                                                                                    ? theme.palette.text.primary
                                                                                    : theme.palette.grey[200],
                                                                            bgcolor:
                                                                                theme.palette.mode === "light"
                                                                                    ? theme.palette.background.paper
                                                                                    : theme.palette.background.default,
                                                                            boxShadow:
                                                                                theme.palette.mode === "light"
                                                                                    ? "0 1px 3px rgba(0,0,0,0.08)"
                                                                                    : "0 1px 3px rgba(0,0,0,0.4)",
                                                                            transition: "all 0.25s ease-in-out",
                                                                            "&:hover": {
                                                                                bgcolor: theme.palette.primary.main,
                                                                                color: theme.palette.primary.contrastText,
                                                                                borderColor: theme.palette.primary.main,
                                                                                transform: "translateY(-2px)",
                                                                                boxShadow:
                                                                                    theme.palette.mode === "light"
                                                                                        ? "0 3px 8px rgba(0,0,0,0.15)"
                                                                                        : "0 3px 8px rgba(0,0,0,0.6)",
                                                                            },
                                                                            "&:active": {
                                                                                transform: "scale(0.96)",
                                                                            },
                                                                        })}
                                                                    >
                                                                        <AddIcon fontSize="small" />
                                                                    </IconButton>

                                                                    {/* ⏳ Loader inline */}
                                                                    {updatingItemId === item.itemId && (
                                                                        <Box
                                                                            sx={{
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                ml: 1,
                                                                                minWidth: 26,
                                                                            }}
                                                                        >
                                                                            <Loader message="" loaderSize={26} />
                                                                        </Box>
                                                                    )}
                                                                </Stack>

                                                            </Box>
                                                            {/* RIGHT SIDE: DELETE BUTTON */}
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: { xs: "center", sm: "flex-end" },
                                                                    mt: { xs: 1, sm: 0 },
                                                                    width: { xs: "100%", sm: "auto" },
                                                                }}
                                                            >
                                                                {/* Full button on larger screens */}
                                                                {/* <Box sx={{ display: { xs: "none", sm: "flex" } }}>
                                                                    <Button
                                                                        size="small"
                                                                        startIcon={<DeleteIcon />}
                                                                        onClick={() => removeItem(item.itemId, item.qty)}
                                                                        variant="outlined"
                                                                        color="error"
                                                                        sx={{
                                                                            fontSize: "0.85rem",
                                                                            py: 0.5,
                                                                            px: 1.2,
                                                                            borderRadius: 2,
                                                                            textTransform: "uppercase",
                                                                        }}
                                                                    >
                                                                        Remove
                                                                    </Button>
                                                                </Box> */}

                                                                {/* Icon-only on mobile */}
                                                                <Box sx={{ display: { xs: "flex", sm: "none" } }}>
                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => removeItem(item.itemId, item.qty)}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </Box>
                                                        </Stack>


                                                    </ListItem>
                                                </React.Fragment>

                                            ))}
                                        </List>

                                        <Box
                                            sx={{
                                                pr: 1,
                                                pl: 1,
                                                mt: 2,
                                                display: "flex",
                                                flexDirection: { xs: "column", sm: "row" },
                                                gap: 1,
                                                alignItems: { xs: "stretch", sm: "center" },
                                            }}
                                        >
                                            <TextField
                                                label="Coupon code"
                                                value={coupon}
                                                size="small"
                                                onChange={(e) => setCoupon(e.target.value)}
                                                fullWidth
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <Button
                                                                size="small"
                                                                onClick={applyCoupon}
                                                                disabled={!coupon.trim()}
                                                                sx={{
                                                                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                                                                    minWidth: { xs: 60, sm: 70 },
                                                                }}
                                                            >
                                                                Apply
                                                            </Button>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />

                                            {appliedCoupon && (
                                                <Card
                                                    variant="outlined"
                                                    sx={{
                                                        width: { xs: "100%", sm: "auto" },
                                                        p: 1,
                                                    }}
                                                >
                                                    <CardContent
                                                        sx={{
                                                            display: "flex",
                                                            gap: 1,
                                                            alignItems: "center",
                                                            flexWrap: "wrap",
                                                            p: 0,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
                                                        >
                                                            Applied: {appliedCoupon.label}
                                                        </Typography>
                                                        <Button
                                                            size="small"
                                                            onClick={clearCoupon}
                                                            sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </CardContent>
                                                </Card>
                                            )}
                                        </Box>
                                    </CardContent>

                                </Card>
                            </Grid>

                            {/* RIGHT SIDE */}
                            <Grid item xs={12} md={4}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6">Price Details</Typography>
                                        <Box sx={{ mt: 2 }}>
                                            <Stack spacing={1}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2">Subtotal</Typography>
                                                    <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2">Discount</Typography>
                                                    <Typography variant="body2">-{formatCurrency(discountAmount)}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2">Shipping</Typography>
                                                    <Typography variant="body2">{shipping === 0 ? 'Free' : formatCurrency(shipping)}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2">Tax</Typography>
                                                    <Typography variant="body2">{formatCurrency(tax)}</Typography>
                                                </Box>
                                                <Divider sx={{ my: 1 }} />
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total</Typography>
                                                    <Typography variant="h6">{formatCurrency(total)}</Typography>
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    size="large"
                                                    startIcon={<ShoppingCartCheckoutIcon />}
                                                    disabled={items.length === 0}
                                                    onClick={handleConfirmOrder}
                                                >
                                                    Confirm Order
                                                </Button>
                                                <Button variant="outlined" startIcon={<LocalShippingIcon />} onClick={() => alert('Estimate shipping clicked')}>
                                                    Estimate Shipping
                                                </Button>
                                            </Stack>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Stack>
                </Container>
            </Box>

            {/* Address Dialog */}
            <Dialog open={addressDialogOpen} onClose={() => setAddressDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Select Shipping Address</DialogTitle>
                <DialogContent>
                    <List>
                        {addresses.map((addr) => (
                            <ListItem
                                key={addr.id}
                                button
                                selected={selectedAddress?.id === addr.id}
                                onClick={() => handleSelectAddress(addr)}
                            >
                                <ListItemAvatar>
                                    <Avatar>
                                        <LocationOnIcon />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={addr.name}
                                    secondary={`${addr.addressLine}, ${addr.city}, ${addr.country}`}
                                />
                            </ListItem>
                        ))}
                    </List>

                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>Add New Address</Typography>
                        <Stack spacing={1}>
                            <TextField label="Name" size="small" value={newAddress.name} onChange={(e) => setNewAddress({ ...newAddress, name: e.target.value })} />
                            <TextField label="Phone" size="small" value={newAddress.phone} onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })} />
                            <TextField label="Address Line" size="small" value={newAddress.addressLine} onChange={(e) => setNewAddress({ ...newAddress, addressLine: e.target.value })} />
                            <TextField label="City" size="small" value={newAddress.city} onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })} />
                            <TextField label="Country" size="small" value={newAddress.country} onChange={(e) => setNewAddress({ ...newAddress, country: e.target.value })} />
                            <Button variant="contained" onClick={handleAddNewAddress}>Add Address</Button>
                        </Stack>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddressDialogOpen(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
